import { supabase } from '@/integrations/supabase/client';

const REFERRAL_STORAGE_KEY = 'solr_referral_code';
const REFERRAL_EXPIRY_DAYS = 30;

interface ReferralData {
  code: string;
  timestamp: number;
}

export const referralTracking = {
  // Store referral code from URL
  captureFromUrl: () => {
    const params = new URLSearchParams(window.location.search);
    const refCode = params.get('ref');
    
    if (refCode) {
      const referralData: ReferralData = {
        code: refCode,
        timestamp: Date.now(),
      };
      localStorage.setItem(REFERRAL_STORAGE_KEY, JSON.stringify(referralData));
      console.log('Referral code captured:', refCode);
    }
  },

  // Get stored referral code if not expired
  getStoredReferral: (): string | null => {
    try {
      const stored = localStorage.getItem(REFERRAL_STORAGE_KEY);
      if (!stored) return null;

      const data: ReferralData = JSON.parse(stored);
      const expiryTime = REFERRAL_EXPIRY_DAYS * 24 * 60 * 60 * 1000;
      
      // Check if expired
      if (Date.now() - data.timestamp > expiryTime) {
        localStorage.removeItem(REFERRAL_STORAGE_KEY);
        return null;
      }

      return data.code;
    } catch (error) {
      console.error('Error reading referral code:', error);
      return null;
    }
  },

  // Clear stored referral after successful conversion
  clearReferral: () => {
    localStorage.removeItem(REFERRAL_STORAGE_KEY);
  },

  // Track referral conversion
  trackConversion: async (orderId: string, orderTotal: number) => {
    const refCode = referralTracking.getStoredReferral();
    if (!refCode) return;

    try {
      // Find referral link by code
      const { data: referralLink, error: linkError } = await supabase
        .from('referral_links')
        .select('id, ambassador_id')
        .eq('referral_code', refCode)
        .single();

      if (linkError || !referralLink) {
        console.log('Referral link not found');
        return;
      }

      // Calculate commission (10% of order total)
      const commission = orderTotal * 0.1;

      // Create referral conversion record
      const { error: conversionError } = await supabase
        .from('referral_conversions')
        .insert({
          referral_link_id: referralLink.id,
          commission_amount: commission,
        });

      if (conversionError) {
        console.error('Error tracking referral conversion:', conversionError);
        return;
      }

      // Create referral reward
      const { data: userData } = await supabase.auth.getUser();
      
      const { error: rewardError } = await supabase
        .from('referral_rewards')
        .insert([{
          referrer_id: referralLink.ambassador_id,
          referred_id: userData?.user?.id || null,
          reward_amount: commission,
          status: 'pending',
        }]);

      if (rewardError) {
        console.error('Error creating referral reward:', rewardError);
      }

      // Clear referral after successful tracking
      referralTracking.clearReferral();
      console.log('Referral conversion tracked successfully');
    } catch (error) {
      console.error('Error in referral tracking:', error);
    }
  },

  // Initialize referral tracking on app load
  initialize: () => {
    referralTracking.captureFromUrl();
  },
};